import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
} from "chart.js";
import moment from "moment";
import { DASHBOARD_PALETTE } from "../../utils/palette.token";
import { IRegistationChart } from "./type";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip
);

/** Sắp xếp theo ngày; nhãn trục rút gọn, tooltip dùng ngày đủ */
function prepareRegistrationSeries(raw: Record<string, number> | undefined) {
  if (!raw || typeof raw !== "object") {
    return {
      labels: [] as string[],
      labelsFull: [] as string[],
      values: [] as number[],
    };
  }
  const rows = Object.entries(raw).map(([key, value]) => {
    const m = moment(key, "DD/MM/YYYY", true);
    const sortKey = m.isValid() ? m.valueOf() : 0;
    return { sortKey, key, m, value: Number(value) || 0 };
  });
  rows.sort((a, b) => a.sortKey - b.sortKey);

  const validYears = new Set(
    rows.filter((r) => r.m.isValid()).map((r) => r.m.year())
  );
  const singleYear = validYears.size <= 1;

  const labels = rows.map((r) => {
    if (!r.m.isValid()) return r.key;
    return singleYear ? r.m.format("DD/MM") : r.m.format("DD/MM/YY");
  });
  const labelsFull = rows.map((r) =>
    r.m.isValid() ? r.m.format("DD/MM/YYYY") : r.key
  );
  const values = rows.map((r) => r.value);

  return { labels, labelsFull, values };
}

export const RegistationChart = ({ data }: IRegistationChart) => {
  const { labels, labelsFull, values } = useMemo(
    () => prepareRegistrationSeries(data as Record<string, number> | undefined),
    [data]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: "index" as const,
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: DASHBOARD_PALETTE.surfaceElevated,
          titleColor: DASHBOARD_PALETTE.textPrimary,
          bodyColor: DASHBOARD_PALETTE.textSecondary,
          borderColor: DASHBOARD_PALETTE.borderAccent,
          borderWidth: 1,
          padding: 12,
          cornerRadius: 8,
          displayColors: false,
          callbacks: {
            title: (items: { dataIndex: number }[]) => {
              const i = items[0]?.dataIndex;
              if (i === undefined) return "";
              return labelsFull[i] ?? "";
            },
          },
        },
      },
      datasets: {
        bar: {
          categoryPercentage: 0.72,
          barPercentage: 0.75,
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
            drawBorder: false,
          },
          ticks: {
            color: DASHBOARD_PALETTE.muted,
            font: {
              family: "Montserrat",
              size: 9,
            },
            maxRotation: 0,
            minRotation: 0,
            autoSkip: true,
            autoSkipPadding: 6,
            maxTicksLimit: 10,
          },
        },
        y: {
          grid: {
            color: DASHBOARD_PALETTE.chartGridLineGreen,
            drawBorder: false,
          },
          ticks: {
            color: DASHBOARD_PALETTE.muted,
            font: {
              family: "Montserrat",
              size: 11,
            },
          },
        },
      },
    }),
    [labelsFull]
  );

  const dataChart = {
    labels,
    datasets: [
      {
        label: "Users",
        data: values,
        backgroundColor: (context: any) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 200);
          gradient.addColorStop(0, "rgba(3, 194, 149, 0.9)");
          gradient.addColorStop(1, "rgba(3, 194, 149, 0.3)");
          return gradient;
        },
        borderRadius: 6,
        borderSkipped: false,
        maxBarThickness: 36,
      },
    ],
  };

  return <Bar options={options} data={dataChart as any} />;
};
